import type { FC, ReactNode } from 'react'
import type { GalleryImage } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface ProjectGalleryProps {
    images: Array<GalleryImage>
    className?: string
}

/**
 * Screenshots for a case study. Add them to a project's `gallery` in
 * src/content/projects.ts; with no images this renders nothing.
 */
const ProjectGallery: FC<ProjectGalleryProps> = ({ images, className }): ReactNode => {
    if (images.length === 0) return null

    return (
        <section aria-label='Screenshots' className={cn('grid grid-cols-1 gap-8 md:grid-cols-2', className)}>
            {images.map((image, index) => {
                const isRemote = /^https?:\/\//.test(image.src)
                const lead = index === 0 && images.length % 2 === 1

                return (
                    <figure key={image.src} className={cn('flex flex-col gap-3', lead && 'md:col-span-2')}>
                        <div className='relative aspect-[16/10] overflow-hidden border border-border bg-surface'>
                            <Image
                                src={image.src}
                                alt={image.alt}
                                fill
                                sizes={lead ? '(min-width: 768px) 80vw, 100vw' : '(min-width: 768px) 40vw, 100vw'}
                                unoptimized={isRemote}
                                className='object-cover'
                            />
                        </div>

                        {image.caption && (
                            <figcaption className='font-mono text-caption text-muted-foreground'>
                                Fig. {String(index + 2).padStart(2, '0')} / {image.caption}
                            </figcaption>
                        )}
                    </figure>
                )
            })}
        </section>
    )
}

export default ProjectGallery
